import { plain } from '../../lib/labels';
import { useState } from 'react';
import {
  Alert,
  Card,
  Typography,
  Space,
  Input,
  Button,
  Modal,
  Select,
  InputNumber,
  Popconfirm,
  Tag,
  Table,
  Descriptions,
  Dropdown,
  App,
  Empty,
  Tabs,
} from 'antd';
import {
  StopOutlined,
  CheckCircleOutlined,
  LogoutOutlined,
  DeleteOutlined,
  RollbackOutlined,
  EyeOutlined,
  MoreOutlined,
  CopyOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { QueryError } from '../../lib/QueryState';
import { errMsg } from '../../lib/errors';
import { PersonAvatar } from '../../lib/PersonAvatar';
import { UserWalletTab, UserRechargesTab, UserCallsTab } from './UserDetailTabs';
import { ReferralTab } from '../listeners/ListenerDetailTabs';
import { ActivityLogsTab } from '../logs/ActivityTimeline';
import { adminLinks } from '../../lib/adminLinks';
import { ListenerName, userLabel, userSecondary, userText, validDisplayName } from '../../lib/listenerName';
import { usePermissions } from '../../auth/store';
import { Can } from '../../rbac/RequirePermission';
import { formatCallCharge, formatCoins, formatRupees } from '../../lib/money';
import {
  ADMIN_CREDIT_MAX_COINS,
  EMPTY_WALLET_ADJUST,
  newAdjustKey,
  walletAdjustBody,
  walletAdjustError,
  type Direction,
  type WalletAdjustForm,
} from './walletAdjust';
import { VerificationAdminControl } from './VerificationAdminControl';
import { GenderControl } from './GenderControl';
import { UserAgencyControl } from './UserAgencyControl';

export interface UserListItem {
  id: string;
  handle: string;
  /** The user's own Display Name (users.display_name) — absent on older APIs. */
  displayName?: string | null;
  avatarSeed: string;
  phoneLast4: string;
  /** Full number — `null` unless this admin holds `users.reveal_phone`. */
  phone?: string | null;
  languagePref: string;
  gender: string | null;
  totalCalls?: number;
  blockedAt?: string | null;
  /** Set when the account is also a listener. */
  listener?: { id: string; status: string } | null;
  onboardingStep: number;
  onboardingCompleted: boolean;
  onboardingLabel: string;
  createdAt: string;
  deletedAt: string | null;
}


interface UserDetail extends UserListItem {
  /**
   * Full phone number, or `null` when this admin may not see it.
   *
   * The API decides: it is populated only for callers holding
   * `users.reveal_phone`, so an `ops` admin opening the same drawer gets `null`
   * and falls back to the masked last-4.
   */
  phone: string | null;
  onboardingCompletedAt: string | null;
  concernTags: string[];
  /** The coin wallet — what the user spends. Authoritative. */
  balanceCoins?: number;
  /** The frozen pre-migration paise wallet (0 after migration). */
  legacyBalancePaise?: string;
  /** @deprecated compat — use balanceCoins. */
  balancePaise: string;
  totalCalls: number;
  openModerationFlags: number;
  blockedAt: string | null;
  blockReason: string | null;
  tokensInvalidatedAt: string | null;
  /** Admin-flagged test account: its calls are verification calls (never billed). */
  isCallVerificationAdmin?: boolean;
  callVerificationSetAt?: string | null;
  listener: { id: string; status: string; tier: string; displayName: string } | null;
  recentCalls: Array<{
    id: string;
    listenerId: string;
    mode: 'audio' | 'video';
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    durationSeconds: number;
    /** Authoritative caller charge, whole coins. */
    totalChargedCoins?: number | null;
    /** @deprecated — fallback only when totalChargedCoins is absent. */
    totalChargedPaise: string;
  }>;
}

/**
 * One user's complete detail — header (name, balance, account actions),
 * status alerts, and the Overview / Wallet / Recharges / Calls / Referrals
 * tabs — plus the dialogs those actions open. Rendered in the right-hand pane
 * of the Users page and, through the layout's PersonPane, beside ANY page a
 * user is clicked on (Calls, Recharges, Listeners…), so the admin never has to
 * leave the list they are working in.
 */
export function UserDetailView({ id: selectedId, onClose }: { id: string; onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [form, setForm] = useState<WalletAdjustForm>(EMPTY_WALLET_ADJUST);
  // The Idempotency-Key for the adjustment currently in the dialog — see newAdjustKey.
  const [adjustKey, setAdjustKey] = useState<string>(newAdjustKey);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealReason, setRevealReason] = useState('');
  const [revealResult, setRevealResult] = useState<string | null>(null);
  // Action-level permissions. Each is checked independently — opening a user
  // never implies being allowed to adjust their wallet or delete them, and the
  // API enforces exactly the same keys.
  const { can } = usePermissions();
  const canRevealPhone = can('users.reveal_phone');
  const canForceLogout = can('users.force_logout');
  const canDeleteUser = can('users.delete');
  const canViewLedger = can('users.wallet.view');

  const detail = useQuery({
    queryKey: ['admin', 'user-detail', selectedId],
    queryFn: async () =>
      (await api.get<UserDetail>(`/admin/users/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  const adjust = useMutation({
    mutationFn: async () => {
      if (!selectedId) return null;
      return (
        await api.post<{ ok: true; duplicate: boolean; balanceCoins: number }>(
          `/admin/users/${selectedId}/wallet-adjust`,
          walletAdjustBody(form),
          { headers: { 'idempotency-key': adjustKey } },
        )
      ).data;
    },
    onSuccess: (res) => {
      const what = form.direction === 'credit' ? 'Credited' : 'Debited';
      message.success(
        res?.duplicate
          ? 'Already applied — this adjustment was not repeated.'
          : `${what} ${formatCoins(form.amountCoins)}. New balance ${formatCoins(res?.balanceCoins ?? 0)}.`,
      );
      setAdjustOpen(false);
      setForm(EMPTY_WALLET_ADJUST);
      setAdjustKey(newAdjustKey());
      qc.invalidateQueries({ queryKey: ['admin', 'user-detail', selectedId] });
      qc.invalidateQueries({ queryKey: ['admin', 'user-ledger', selectedId] });
    },
    onError: (e: unknown) => {
      message.error(errMsg(e, 'Could not adjust wallet'));
    },
  });

  // ----- Account-control mutations -----------------------------------
  // Each follows the same pattern: POST, then invalidate detail + search so
  // the UI immediately reflects new blockedAt / deletedAt / etc.

  function showErr(label: string) {
    return (e: unknown) => {
      message.error(errMsg(e, label));
    };
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin', 'user-detail', selectedId] });
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  }

  const block = useMutation({
    mutationFn: async (reason: string) =>
      (await api.post(`/admin/users/${selectedId}/block`, { reason })).data,
    onSuccess: () => {
      message.success('User blocked');
      setBlockOpen(false);
      setBlockReason('');
      invalidate();
    },
    onError: showErr('Could not block user'),
  });

  const unblock = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${selectedId}/unblock`)).data,
    onSuccess: () => {
      message.success('User unblocked');
      invalidate();
    },
    onError: showErr('Could not unblock user'),
  });

  const forceLogout = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${selectedId}/force-logout`)).data,
    onSuccess: () => {
      message.success('All sessions invalidated — user will be signed out everywhere on next request');
      invalidate();
    },
    onError: showErr('Could not force-logout user'),
  });

  const deleteUser = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${selectedId}/delete`)).data,
    onSuccess: () => {
      message.success('User soft-deleted (30-day grace before hard-wipe)');
      invalidate();
    },
    onError: showErr('Could not delete user'),
  });

  const restore = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${selectedId}/restore`)).data,
    onSuccess: () => {
      message.success('User restored');
      invalidate();
    },
    onError: showErr('Could not restore user'),
  });

  const revealPhone = useMutation({
    mutationFn: async (reason: string) =>
      (await api.post<{ phone: string; revealedAt: string }>(
        `/admin/users/${selectedId}/reveal-phone`,
        { reason },
      )).data,
    onSuccess: (data) => {
      setRevealResult(data.phone);
      setRevealOpen(false);
      setRevealReason('');
      message.warning('Phone revealed — this action was audit-logged with your reason.');
    },
    onError: showErr('Could not reveal phone'),
  });

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {detail.data ? (
  <Space align="center">
    <PersonAvatar
      name={validDisplayName(detail.data.displayName) ?? detail.data.handle}
      seed={detail.data.avatarSeed ?? detail.data.id}
    />
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <span>{userLabel(detail.data)}</span>
      {userSecondary(detail.data) ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
          {userSecondary(detail.data)}
        </Typography.Text>
      ) : null}
    </span>
    <Typography.Text type="secondary" copyable={{ text: detail.data.id }} style={{ fontSize: 12, fontWeight: 400 }}>
      {detail.data.id.slice(0, 8)}
    </Typography.Text>
  </Space>
) : (
  'User'
)}
        </Typography.Title>
        <Space wrap>
          {detail.data ? (
  <Space>
    <Tag color="processing">
      {typeof detail.data.balanceCoins === 'number'
        ? formatCoins(detail.data.balanceCoins)
        : '— coins'}
    </Tag>
    <Can permission="users.wallet.adjust">
      <Button type="primary" size="small" onClick={() => setAdjustOpen(true)}>
        Credit / debit coins
      </Button>
    </Can>
    {/* Status flips define which action makes sense. We render
        the most likely / safest next action as a top-level
        button and tuck the rest behind a kebab menu. */}
    {detail.data.blockedAt ? (
      <Can permission="users.block"><Popconfirm
        title="Unblock this user?"
        description="They can sign in and use the app again. Listener suspension (if any) stays."
        onConfirm={() => unblock.mutate()}
        okText="Unblock"
        okButtonProps={{ loading: unblock.isPending }}
      >
        <Button size="small" icon={<CheckCircleOutlined />} type="primary">
          Unblock
        </Button>
      </Popconfirm></Can>
    ) : !detail.data.deletedAt ? (
      <Can permission="users.block">
        <Button
          size="small"
          icon={<StopOutlined />}
          danger
          onClick={() => setBlockOpen(true)}
        >
          Block
        </Button>
      </Can>
    ) : null}
    {detail.data.deletedAt ? (
      <Can permission="users.delete"><Popconfirm
        title="Restore this user?"
        description="Only works within the 30-day grace window. After that the hard-wipe job has zeroed PII."
        onConfirm={() => restore.mutate()}
        okText="Restore"
        okButtonProps={{ loading: restore.isPending }}
      >
        <Button size="small" icon={<RollbackOutlined />}>
          Restore
        </Button>
      </Popconfirm></Can>
    ) : null}
    <Dropdown
      placement="bottomRight"
      menu={{
        items: [
          !canForceLogout
            ? null
            : {
            key: 'force-logout',
            icon: <LogoutOutlined />,
            label: 'Force sign-out everywhere',
            onClick: () => {
              Modal.confirm({
                title: 'Force sign-out?',
                content:
                  'Every JWT issued before now will be rejected on the next API call. The user lands back on the OTP screen on all devices.',
                okText: 'Force sign-out',
                okButtonProps: { danger: true },
                onOk: () => forceLogout.mutate(),
              });
            },
          },
          canRevealPhone
            ? {
                key: 'reveal-phone',
                icon: <EyeOutlined />,
                label: 'Reveal real phone (audited)',
                onClick: () => setRevealOpen(true),
              }
            : null,
          { type: 'divider' as const },
          detail.data.deletedAt || !canDeleteUser
            ? null
            : {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete account (DPDP soft-delete)',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: 'Soft-delete this user?',
                    content:
                      'Signs them out immediately and schedules hard PII wipe in 30 days. Within that window the Restore button reverses this.',
                    okText: 'Delete',
                    okButtonProps: { danger: true },
                    onOk: () => deleteUser.mutate(),
                  });
                },
              },
        ].filter(Boolean),
      }}
    >
      <Button size="small" icon={<MoreOutlined />} />
    </Dropdown>
  </Space>
) : null}
          <Button type="text" icon={<CloseOutlined />} aria-label="Close details" onClick={onClose} />
        </Space>
      </div>
      {detail.data ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {detail.data.blockedAt && (
            <Alert
              type="error"
              showIcon
              message={`Blocked on ${new Date(detail.data.blockedAt).toLocaleString()}`}
              description={detail.data.blockReason ?? 'No reason recorded.'}
            />
          )}
          {detail.data.deletedAt && (
            <Alert
              type="warning"
              showIcon
              message={`Soft-deleted on ${new Date(detail.data.deletedAt).toLocaleString()}`}
              description="30-day grace window — PII wipe runs after that, restore impossible afterwards."
            />
          )}
          {detail.data.tokensInvalidatedAt && !detail.data.blockedAt && !detail.data.deletedAt && (
            <Alert
              type="info"
              showIcon
              message="Sessions were force-invalidated"
              description={`All tokens issued before ${new Date(
                detail.data.tokensInvalidatedAt,
              ).toLocaleString()} are rejected. The user will sign in again next time they open the app.`}
            />
          )}
          <Tabs
            size="small"
            destroyOnHidden
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="User ID">{detail.data.id}</Descriptions.Item>
            {/* Full number for authorized roles; masked fallback otherwise. The
                reason-gated Reveal action stays available either way — it is the
                one that records WHY a number was looked at. */}
            <Descriptions.Item label="Phone">
              {detail.data.phone ? (
                <Space size={6}>
                  <Typography.Text copyable={{ text: detail.data.phone }}>
                    {detail.data.phone}
                  </Typography.Text>
                </Space>
              ) : (
                <>••• {detail.data.phoneLast4}</>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Language">{detail.data.languagePref}</Descriptions.Item>
            <Descriptions.Item label="Gender">
              <GenderControl
                userId={detail.data.id}
                handle={detail.data.handle}
                label={userText(detail.data)}
                gender={detail.data.gender}
                onChanged={invalidate}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Joined">
              {new Date(detail.data.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Deleted">
              {detail.data.deletedAt ? new Date(detail.data.deletedAt).toLocaleString() : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Registration" span={2}>
              {detail.data.onboardingCompleted ? (
                <Space>
                  <Tag color="success">Completed</Tag>
                  {detail.data.onboardingCompletedAt && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(detail.data.onboardingCompletedAt).toLocaleString()}
                    </Typography.Text>
                  )}
                </Space>
              ) : (
                <Tag color="warning">Left at: {detail.data.onboardingLabel}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Coin balance">
              {typeof detail.data.balanceCoins === 'number'
                ? formatCoins(detail.data.balanceCoins)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Old wallet balance">
              {formatRupees(detail.data.legacyBalancePaise ?? null)}
            </Descriptions.Item>
            <Descriptions.Item label="Total calls">{detail.data.totalCalls}</Descriptions.Item>
            <Descriptions.Item label="Open safety reports">
              {detail.data.openModerationFlags > 0 ? (
                <Tag color="error">{detail.data.openModerationFlags}</Tag>
              ) : (
                <Tag>0</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Test caller (calls are free)" span={2}>
              <VerificationAdminControl
                userId={detail.data.id}
                handle={detail.data.handle}
                label={userText(detail.data)}
                enabled={detail.data.isCallVerificationAdmin === true}
                setAt={detail.data.callVerificationSetAt ?? null}
                onChanged={invalidate}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Agency" span={2}>
              <UserAgencyControl userId={detail.data.id} />
            </Descriptions.Item>
            <Descriptions.Item label="Concerns" span={2}>
              {detail.data.concernTags.length > 0
                ? detail.data.concernTags.map((c) => <Tag key={c}>{c}</Tag>)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Listener" span={2}>
              {detail.data.listener ? (
                <Space wrap>
                  <ListenerName
                    listener={{ displayName: detail.data.listener.displayName, handle: detail.data.handle, id: detail.data.listener.id }}
                    listenerId={detail.data.listener.id}
                    secondary="none"
                  />
                  <Tag color="magenta">{plain(detail.data.listener.status)}</Tag>
                  <Tag>{plain(detail.data.listener.tier)}</Tag>
                </Space>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>

                    <Space wrap>
                      {detail.data.listener && (
                        <Button onClick={() => navigate(adminLinks.listener(detail.data.listener!.id))}>
                          Open listener profile
                        </Button>
                      )}
                      {detail.data.openModerationFlags > 0 && (
                        <Button onClick={() => navigate(`/moderation?q=${encodeURIComponent(detail.data.handle)}`)}>
                          Open safety reports
                        </Button>
                      )}
                    </Space>
                    <Card size="small" title="Recent calls (last 10)">
                      {detail.data.recentCalls.length === 0 ? (
                        <Empty description="No calls yet." />
                      ) : (
                        <Table
                          rowKey="id"
                          pagination={false}
                          size="small"
                          dataSource={detail.data.recentCalls}
                          columns={[
                            { title: 'When', dataIndex: 'startedAt', render: (s: string | null) => (s ? new Date(s).toLocaleString() : '—') },
                            { title: 'Mode', dataIndex: 'mode', width: 80 },
                            { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag>{plain(v)}</Tag> },
                            { title: 'Duration (s)', dataIndex: 'durationSeconds', width: 100 },
                            {
                              title: 'Charged',
                              key: 'charged',
                              render: (_: unknown, row: UserDetail['recentCalls'][number]) => formatCallCharge(row),
                              width: 100,
                            },
                            {
                              title: '',
                              key: 'open',
                              width: 70,
                              render: (_: unknown, row: UserDetail['recentCalls'][number]) => (
                                <Button size="small" onClick={() => navigate(adminLinks.call(row.id))}>
                                  Open
                                </Button>
                              ),
                            },
                          ]}
                        />
                      )}
                    </Card>
                  </Space>
                ),
              },
              ...(canViewLedger
                ? [{ key: 'wallet', label: 'Wallet', children: <UserWalletTab userId={detail.data.id} /> }]
                : []),
              ...(can('payments.view')
                ? [{ key: 'recharges', label: 'Recharges', children: <UserRechargesTab userId={detail.data.id} /> }]
                : []),
              ...(can('calls.view')
                ? [{ key: 'calls', label: 'Calls', children: <UserCallsTab userId={detail.data.id} /> }]
                : []),
              ...(can('referral.view')
                ? [{ key: 'referrals', label: 'Referrals', children: <ReferralTab userId={detail.data.id} /> }]
                : []),
              ...(can('logs.view')
                ? [{ key: 'activity', label: 'Activity logs', children: <ActivityLogsTab userId={detail.data.id} /> }]
                : []),
            ]}
          />
        </Space>
      ) : detail.isError ? (
        <QueryError error={detail.error} what="this user" onRetry={() => void detail.refetch()} />
      ) : detail.isLoading ? (
        <Typography.Text type="secondary">Loading user…</Typography.Text>
      ) : null}

      <Modal
        title={`Wallet adjust — ${detail.data ? userText(detail.data) : ''}`}
        open={adjustOpen}
        onCancel={() => setAdjustOpen(false)}
        onOk={() => {
          // Belt and braces with the loading button: never two in flight.
          if (!adjust.isPending) adjust.mutate();
        }}
        okText={form.direction === 'credit' ? 'Credit coins' : 'Debit coins'}
        okButtonProps={{
          disabled: walletAdjustError(form) !== null,
          loading: adjust.isPending,
        }}
        cancelButtonProps={{ disabled: adjust.isPending }}
        maskClosable={!adjust.isPending}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Typography.Text>
            User <strong>{detail.data ? userText(detail.data) : '?'}</strong> — current balance{' '}
            <strong>
              {typeof detail.data?.balanceCoins === 'number' ? formatCoins(detail.data.balanceCoins) : '—'}
            </strong>
          </Typography.Text>
          <Tag color={form.direction === 'credit' ? 'success' : 'error'}>
            {form.direction.toUpperCase()}
          </Tag>
          <Select<Direction>
            style={{ width: '100%' }}
            value={form.direction}
            onChange={(direction) => setForm({ ...form, direction })}
            disabled={adjust.isPending}
            options={[
              { value: 'credit', label: 'Credit (add coins to the user wallet)' },
              { value: 'debit', label: 'Debit (remove coins from the user wallet)' },
            ]}
          />
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            precision={0}
            placeholder="Coins to credit (whole number)"
            addonAfter="coins"
            value={form.amountCoins || null}
            onChange={(v) => setForm({ ...form, amountCoins: v ?? 0 })}
            status={form.amountCoins && walletAdjustError(form)?.startsWith('Maximum') ? 'error' : undefined}
            disabled={adjust.isPending}
          />
          {form.direction === 'credit' && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Maximum {ADMIN_CREDIT_MAX_COINS} coins per transaction. Recorded as an Admin credit in the
              user&apos;s history.
            </Typography.Text>
          )}
          {form.direction === 'debit' && typeof detail.data?.balanceCoins === 'number' && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Current balance {formatCoins(detail.data.balanceCoins)} — a debit below 0 is rejected.
            </Typography.Text>
          )}
          <Input.TextArea
            rows={3}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Reason / note (≥ 10 chars) — written to audit log + ledger metadata"
            maxLength={500}
            disabled={adjust.isPending}
          />
          {(form.amountCoins > 0 || form.reason) && walletAdjustError(form) ? (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {walletAdjustError(form)}
            </Typography.Text>
          ) : null}
        </Space>
      </Modal>

      {/* Block-with-reason modal */}
      <Modal
        title={`Block ${detail.data ? userText(detail.data) : ''}`}
        open={blockOpen}
        onCancel={() => {
          setBlockOpen(false);
          setBlockReason('');
        }}
        onOk={() => block.mutate(blockReason)}
        okText="Block user"
        okButtonProps={{
          danger: true,
          disabled: blockReason.trim().length < 10,
          loading: block.isPending,
        }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Blocking is reversible but disruptive"
          description={
            <span style={{ fontSize: 12 }}>
              All active sessions are invalidated. If the account is also an approved listener,
              the listener side is suspended for 365 days. The user sees a 'your account is
              restricted' message on every API call until you unblock.
            </span>
          }
        />
        <Input.TextArea
          rows={3}
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
          placeholder="Reason for block (≥ 10 chars) — captured in the audit log and shown on the User detail."
        />
      </Modal>

      {/* Reveal real phone — gated to super_admin + finance at the API. */}
      <Modal
        title={`Reveal phone for ${detail.data ? userText(detail.data) : ''}`}
        open={revealOpen}
        onCancel={() => {
          setRevealOpen(false);
          setRevealReason('');
        }}
        onOk={() => revealPhone.mutate(revealReason)}
        okText="Reveal phone"
        okButtonProps={{
          danger: true,
          disabled: revealReason.trim().length < 10,
          loading: revealPhone.isPending,
        }}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="PII access — every reveal is audit-logged"
          description={
            <span style={{ fontSize: 12 }}>
              The full phone number will be decrypted from PiiVault and shown ONCE in the next
              modal. Your admin id, the user id, the reason you give, and the moment of access
              are written to the audit log. Use only for refund disputes, fraud investigations,
              or law-enforcement requests.
            </span>
          }
        />
        <Input.TextArea
          rows={3}
          value={revealReason}
          onChange={(e) => setRevealReason(e.target.value)}
          placeholder="Reason (≥ 10 chars) — e.g. 'Refund dispute #4821 / customer asked us to verify'"
        />
      </Modal>

      <Modal
        title="Revealed phone"
        open={!!revealResult}
        onCancel={() => setRevealResult(null)}
        onOk={() => setRevealResult(null)}
        okText="Done"
        cancelButtonProps={{ style: { display: 'none' } }}
        closable={false}
        maskClosable={false}
      >
        {revealResult && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This is the only time it will be shown"
              description="Closing this dialog removes it from the session. Re-revealing writes another audit entry."
            />
            <Input
              value={revealResult}
              readOnly
              size="large"
              style={{ fontFamily: 'monospace', fontSize: 18 }}
              suffix={
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    void navigator.clipboard.writeText(revealResult);
                    message.success('Phone copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              }
            />
          </Space>
        )}
      </Modal>
    </Space>
  );
}
